import { action, computed, observable, runInAction } from "mobx";
import { ObservableSubscription } from "@apollo/client";
import debug from "debug";
import { store, Store } from "@store";
import { BaseStore } from "@store/BaseStore";
import {
  ConsultantQuality_ConsultantNewFragment,
  ConsultantQuality_GetConsultantNew
} from "@graphql";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog/OkCancelDialogOptions";

const log = debug("app:consultants:ConsultantQualityControlStore");

export class QualityChecksStore extends BaseStore {
  rootStore: Store;

  @computed get allQualityChecks() {
    if (!this.consultant) {
      return [];
    }

    return this.consultant.QualityPerCountry.map((q) => q.QualityChecks).selectMany(
      (x) => x
    );
  }

  @computed get activeQualityCheckDocType() {
    if (!this.activeQualityCheck) {
      return null;
    }

    const check = this.allQualityChecks.find(
      (x) =>
        x.Country === this.activeQualityCheck.country &&
        x.Name === this.activeQualityCheck.name
    );

    if (!check) {
      return null;
    }

    return check.DocumentType;
  }

  @computed get canEdit() {
    return Boolean(this.consultant) && !this.consultant.Unfit;
  }

  @observable consultant: ConsultantQuality_ConsultantNewFragment = null;

  @observable activeQualityCheck: {
    country: string;
    name: string;
    type: string;
    referenceId?: string;
    isNationalReference?: boolean;
    templateName?: string;
  } | null = null;

  getConsultant = ConsultantQuality_GetConsultantNew.funcWatch(
    { id: this.consultantId },
    { fetchPolicy: "cache-and-network" }
  );

  private subscription: ObservableSubscription;

  constructor(public readonly consultantId: string) {
    super();

    if (!consultantId) {
      throw new Error("wrong arguments");
    }

    this.rootStore = store();
    this.subscribe();

    (window as any).consultantQualityStore = this;
  }

  subscribe = () => {
    const { snackbars } = this.rootStore;

    this.subscription = this.getConsultant.subscribe({
      next: (value) => {
        if (value.loading) {
          return;
        }

        runInAction(() => {
          this.consultant = value.data.consultantById;
          this.setReady();
        });
      },
      error: (e) => {
        snackbars.error(`Cannot fetch consultant data\nReason: ${e}`);
        this.setError(e);
      }
    });
  };

  getQualityCheck(country: string, name: string) {
    return this.allQualityChecks.find((q) => q.Country === country && q.Name === name);
  }

  @action openQualityCheckEditor = (
    country: string,
    name: string,
    type: string,
    templateName: string
  ) => {
    log("openQualityControlEditor", { country, name });
    const qualityCheck = this.getQualityCheck(country, name);

    if (!qualityCheck) {
      return;
    }

    this.activeQualityCheck = { country, name, type, templateName };
  };

  @action openQualityReferenceEditor = (
    country: string,
    name: string,
    type: string,
    referenceId: string,
    isNationalReference: boolean
  ) => {
    this.activeQualityCheck = { country, name, type, referenceId, isNationalReference };
  };

  @action closeQualityControlEditor = () => {
    log("closeQualityControlEditor");
    this.activeQualityCheck = null;
  };

  @action submitReference = (id: string | undefined, data: any) => {
    const opt = new OkCancelDialogOptions();
    this.rootStore.ui.confirmDialog
      .confirm(opt)
      .then((value) => {
        if (value === "cancel") {
          return;
        }
      })
      .catch((err) => {
        this.rootStore.snackbars.error(`Error \n Reason:  ${err}`);
      });
  };

  @action submitNationalProcurementReference = (id: string | undefined, data: any) => {
    const opt = new OkCancelDialogOptions();
    this.rootStore.ui.confirmDialog
      .confirm(opt)
      .then((value) => {
        if (value === "cancel") {
          return;
        }
      })
      .catch((err) => {
        this.rootStore.snackbars.error(`Error \n Reason:  ${err}`);
      });
  };
}
